import { type FormEvent, type ReactElement, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../../shared/format.js';
import { useFieldErrors } from '../../shared/hooks/useFieldErrors.js';
import { useQuickFilter } from '../../shared/hooks/useQuickFilter.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import {
  Badge,
  Button,
  Card,
  ConfirmButton,
  Dialog,
  EmptyState,
  ErrorAlert,
  Field,
  FilterInput,
  LoadingBlock,
  PageHeader,
  TextInput,
} from '../../shared/ui/index.js';
import { DateRangeCalendar, type PartialDateRange, UNBOUNDED_RANGE } from './DateRangeCalendar.js';
import { useCreateSojourn, useDeleteSojourn, useSojourns } from './sojournHooks.js';

export function SojournsPage(): ReactElement {
  const t = useTranslation();
  const sojourns = useSojourns();
  const deleteSojourn = useDeleteSojourn();
  const [creating, setCreating] = useState(false);

  // Dates are searched as they are displayed — "jul" or "2026", not the ISO
  // form nobody reads off the screen.
  const filter = useQuickFilter(sojourns.data ?? [], (sojourn) => [
    sojourn.name,
    formatDate(sojourn.startDate),
    formatDate(sojourn.endDate),
  ]);

  return (
    <div className="stack stack--loose">
      <PageHeader
        title={t.sojournsPage.title}
        subtitle={t.sojournsPage.subtitle}
        actions={<Button onClick={() => setCreating(true)}>{t.sojournsPage.newSojourn}</Button>}
      />

      <ErrorAlert error={sojourns.error ?? deleteSojourn.error} />

      {sojourns.isPending ? <LoadingBlock /> : null}

      {sojourns.data !== undefined && sojourns.data.length > 0 ? (
        <FilterInput
          label={t.sojournsPage.filterLabel}
          placeholder={t.sojournsPage.filterPlaceholder}
          value={filter.query}
          matchCount={filter.matchCount}
          totalCount={filter.totalCount}
          onChange={filter.setQuery}
        />
      ) : null}

      {sojourns.data?.length === 0 ? (
        <EmptyState
          title={t.sojournsPage.emptyTitle}
          description={t.sojournsPage.emptyDescription}
          action={<Button onClick={() => setCreating(true)}>{t.sojournsPage.newSojourn}</Button>}
        />
      ) : null}

      {filter.isFiltering && filter.matchCount === 0 ? (
        <EmptyState
          title={t.sojournsPage.noMatchesTitle}
          description={t.sojournsPage.noMatchesDescription(filter.query)}
          action={
            <Button variant="secondary" onClick={() => filter.setQuery('')}>
              {t.common.clearFilter}
            </Button>
          }
        />
      ) : null}

      {filter.matchCount > 0 ? (
        <div className="grid">
          {filter.results.map((sojourn) => (
            <Card key={sojourn.id}>
              <div className="stack stack--tight">
                <div className="row row--between">
                  <h2 className="card__title">
                    <Link to={`/sojourns/${sojourn.id}`}>{sojourn.name}</Link>
                  </h2>
                  {sojourn.hasPlan ? <Badge tone="positive">{t.sojournsPage.planned}</Badge> : null}
                </div>
                <p className="card__meta">
                  {formatDate(sojourn.startDate)} → {formatDate(sojourn.endDate)} ·{' '}
                  {t.count.nights(sojourn.nights)}
                </p>
                <p className="card__meta">
                  {t.count.houses(sojourn.houseCount)} · {t.count.guests(sojourn.guestCount)}
                </p>
                <div className="row row--between">
                  <Link to={`/sojourns/${sojourn.id}/plan`}>
                    {sojourn.hasPlan ? t.sojournsPage.viewThePlan : t.sojournsPage.makeAPlan}
                  </Link>
                  <ConfirmButton
                    small
                    label={t.common.delete}
                    onConfirm={() => deleteSojourn.mutate(sojourn.id)}
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : null}

      {creating ? <CreateSojournDialog onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

function CreateSojournDialog({ onClose }: { onClose: () => void }): ReactElement {
  const t = useTranslation();
  const createSojourn = useCreateSojourn();
  const [name, setName] = useState('');
  const [dates, setDates] = useState<PartialDateRange>({ start: null, end: null });
  const fieldError = useFieldErrors(createSojourn.error);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (dates.start === null || dates.end === null) {
      return;
    }
    createSojourn.mutate(
      { name, startDate: dates.start, endDate: dates.end },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog title={t.sojournsPage.createDialogTitle} onClose={onClose}>
      <form className="stack" onSubmit={submit} noValidate>
        <ErrorAlert error={createSojourn.error} />

        <Field
          label={t.sojournsPage.nameLabel}
          hint={t.sojournsPage.nameHint}
          error={fieldError('name')}
        >
          {(props) => (
            <TextInput
              {...props}
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoFocus
              required
            />
          )}
        </Field>

        <DateRangeCalendar bounds={UNBOUNDED_RANGE} value={dates} onChange={setDates} />

        <div className="row row--end">
          <Button variant="secondary" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button
            type="submit"
            loading={createSojourn.isPending}
            disabled={dates.start === null || dates.end === null}
          >
            {t.sojournsPage.create}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
